import type { Carousel, CarouselApi } from '@/components/ui/carousel';
import {
	type Ref,
	onMounted,
	shallowRef,
	ref,
	watchEffect,
	nextTick,
	computed
} from 'vue';

interface CarouselEvents {
	pauseAutoplay?: () => void;
	resumeAutoplay?: () => void;
	onInit?: (carouselApi: CarouselApi | undefined) => void;
}

interface CarouselControlOptions {
	carouselContainerRef: Ref<InstanceType<typeof Carousel> | null>;
	events?: CarouselEvents;
	carouselItemSelectorContainerRef?: Ref<HTMLDivElement | null>;
	cyclic?: boolean;
}

export function useCarouselControl({
	carouselContainerRef,
	events,
	carouselItemSelectorContainerRef,
	cyclic = false
}: CarouselControlOptions) {
	const currentCarouselItemIndex = shallowRef<number>(0);
	const scrollableSlidesNumber = shallowRef<number>(0);

	const updateCurrentIndex = () => {
		const carouselApi = carouselContainerRef.value?.carouselApi;
		carouselApi?.plugins()?.autoplay?.reset?.();
		currentCarouselItemIndex.value = carouselApi?.selectedScrollSnap() ?? 0;
	};

	const scrollTo = (index: number, immediate = false) => {
		let scrollIndex = index;

		if (cyclic) {
			scrollIndex =
				(index + scrollableSlidesNumber.value) % scrollableSlidesNumber.value;
		}

		carouselContainerRef.value?.carouselApi?.scrollTo(scrollIndex, immediate);
	};

	const setupOnInit = (scrollSnapList: number[]) => {
		scrollableSlidesNumber.value = scrollSnapList.length;
		events?.onInit?.(carouselContainerRef.value?.carouselApi);
	};

	function setupEventListeners(api: NonNullable<CarouselApi>) {
		// Remove existing listeners
		api.off('init', () => {});
		api.off('select', () => {});
		api.off('pointerDown', () => {});
		api.off('pointerUp', () => {});

		// Add new listeners
		api.on('init', () => setupOnInit(api.scrollSnapList() ?? []));
		api.on('select', updateCurrentIndex);

		if (events) {
			api.on('pointerDown', events.pauseAutoplay!);
			api.on('pointerUp', events.resumeAutoplay!);
		}
	}

	function updateWidth() {
		if (!carouselItemSelectorContainerRef?.value) return;

		const rect =
			carouselItemSelectorContainerRef.value?.getBoundingClientRect();
		carouselItemSelectorContainerWidth.value = rect?.width || 0;
	}

	const reinitializeCarousel = () => {
		const carouselApi = carouselContainerRef.value?.carouselApi;
		if (carouselApi) {
			setupEventListeners(carouselApi);
			carouselApi.reInit();
			setupOnInit(carouselApi.scrollSnapList());
			updateCurrentIndex();
			updateWidth();
		}
	};

	const carouselItemSelectorContainerWidth = ref(0);

	// Watch for changes that might affect the width
	watchEffect(() => {
		if (
			carouselItemSelectorContainerRef?.value &&
			scrollableSlidesNumber.value > 0
		) {
			nextTick(updateWidth);
		}
	});

	onMounted(() => {
		updateWidth();
		window.addEventListener('resize', updateWidth);
		window.addEventListener('zoom', updateWidth);
	});

	onUnmounted(() => {
		window.removeEventListener('resize', updateWidth);
		window.removeEventListener('zoom', updateWidth);
	});

	const carouselDynamicItemWidth = computed(() => {
		const containerWidth = carouselItemSelectorContainerWidth.value;

		const gap = 12;
		const maxItemWidth = 60;

		if (!containerWidth) return maxItemWidth;

		const totalGapSpace = (scrollableSlidesNumber.value - 1) * gap;
		const availableSpaceForItems = containerWidth - totalGapSpace;
		const calculatedItemWidth = Math.floor(
			availableSpaceForItems / scrollableSlidesNumber.value
		);

		return Math.min(calculatedItemWidth, maxItemWidth);
	});

	onMounted(async () => {
		await until(carouselContainerRef).toBeTruthy();
		const carouselApi = carouselContainerRef.value?.carouselApi;
		if (carouselApi) {
			setupEventListeners(carouselApi);
		}
	});

	return {
		currentCarouselItemIndex,
		scrollableSlidesNumber,
		carouselDynamicItemWidth,
		scrollTo,
		reinitializeCarousel
	};
}
